import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../store/authStore';
import { roleStore, Role } from '../../store/roleStore';
import { useGetUnreadCountsQuery } from '../services/notificationService/__generated__/queries.generated';
import { getFirebaseConfig } from '../../config/firebase-config';

/**
 * Foreground FCM → refresh navbar unread badges only.
 * Chat unread lives on the menu badge (`useUnreadChatCount`); do not toast on
 * route changes or existing unread (Turo-style: badge, no global “new message”).
 */
export const useChatNotifications = () => {
  const auth = useSnapshot(authStore);
  const { role } = useSnapshot(roleStore);
  const userId = auth.user?.id;
  const isAuthenticated = auth.isAuthenticated && !!userId;
  const countRole = role === Role.ADMIN ? Role.GUEST : role;

  const { refetch: refetchUnreadCounts } = useGetUnreadCountsQuery({
    variables: { role: countRole as string },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const initFcmListener = async () => {
      try {
        const { getMessaging, onMessage, isSupported } = await import('firebase/messaging');
        const { initializeApp, getApps } = await import('firebase/app');

        const supported = await isSupported();
        if (!supported) {
          console.debug('[Chat Notifications] Browser does not support Firebase Messaging');
          return null;
        }

        let app = getApps()[0];
        if (!app) {
          app = initializeApp(getFirebaseConfig());
        }

        const messaging = getMessaging(app);

        const unsubscribe = onMessage(messaging, () => {
          console.debug('[Chat Notifications] Received FCM message, refetching unread counts');
          refetchUnreadCounts();
        });

        return unsubscribe;
      } catch (error) {
        console.debug('[Chat Notifications] FCM listener setup failed:', error);
        return null;
      }
    };

    let unsubscribe: (() => void) | null = null;
    initFcmListener().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAuthenticated, refetchUnreadCounts]);
};
